import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    fetchQuizReview,
    fetchCollabQuizAttempts,
    setQuizzesReviewData,
} from "../../vsecurite/vsecuriteSlice";
import Langs, { Localize } from "../../../app/lang/langs";
import moment from "moment";
import "moment/locale/fr";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";

/* ── Palette constants ── */
const ORANGE = "#FF9015";
const ORANGE_LIGHT = "rgba(255,144,21,0.08)";
const ORANGE_GRADIENT = "linear-gradient(135deg, #FF8701, #FFC100)";
const GREEN = "#16a34a";
const GREEN_LIGHT = "rgba(22,163,74,0.08)";
const RED = "#dc2626";
const RED_LIGHT = "rgba(220,38,38,0.07)";
const BLUE = "#1d4ed8";
const BLUE_LIGHT = "rgba(29,78,216,0.07)";
const TEXT_PRIMARY = "#111318";
const TEXT_SECONDARY = "#4b5263";
const TEXT_MUTED = "#8b92a0";
const BORDER_SUBTLE = "rgba(0,0,0,0.06)";
const BG_PAGE = "linear-gradient(160deg, #f7f8fa 0%, #eef0f4 100%)";
const EASE = "cubic-bezier(0.4,0,0.2,1)";

/* ── Animation keyframes ── */
const KEYFRAMES = `
    @keyframes qrFadeInUp {
        from { opacity: 0; transform: translateY(14px); }
        to   { opacity: 1; transform: translateY(0); }
    }
`;

/**
 * QuizReview – Displays a completed quiz for review, showing correct answers,
 * the collab's selections, and explanations for every question.
 */
export default function QuizReview({ quizId, onBack }) {
    const dispatch = useDispatch();
    const [selectedAttemptId, setSelectedAttemptId] = useState(null);

    const reviewData = useSelector((state) => state.vsecurite.quizzes.reviewData);
    const collabQuizAttempts = useSelector((state) => state.vsecurite.quizzes.collabQuizAttempts);
    const loading = useSelector((state) => state.vsecurite.quizzes.loading);
    const lang = useSelector((state) => state.settings.lang);
    const isMobileRedux = useSelector((state) => state.settings.isMobile);

    const theme = useTheme();
    const isSmallQuery = useMediaQuery(theme.breakpoints.down("sm"));
    const isSmall = isMobileRedux === 2 || isSmallQuery;

    /* On quizId change: fetch attempts list and clear review */
    useEffect(() => {
        if (quizId) {
            dispatch(setQuizzesReviewData(null));
            setSelectedAttemptId(null);
            dispatch(fetchCollabQuizAttempts({ id_quiz: quizId }));
        }
    }, [dispatch, quizId]);

    /* Auto-select best attempt once list loads */
    useEffect(() => {
        if (collabQuizAttempts.length && !selectedAttemptId) {
            const best = [...collabQuizAttempts].sort((a, b) => {
                if (Number(b.passed) !== Number(a.passed)) return Number(b.passed) - Number(a.passed);
                return (b.score || 0) - (a.score || 0);
            })[0];
            setSelectedAttemptId(best.id);
        }
    }, [collabQuizAttempts]);

    /* Fetch review whenever selected attempt changes */
    useEffect(() => {
        if (selectedAttemptId) {
            dispatch(fetchQuizReview({ id_attempt: selectedAttemptId }));
        }
    }, [dispatch, selectedAttemptId]);

    const allowScores = reviewData?.quiz?.allow_scores;

    /* Find best attempt id for highlighting */
    const bestAttemptId = collabQuizAttempts.length
        ? [...collabQuizAttempts].sort((a, b) => {
            if (Number(b.passed) !== Number(a.passed)) return Number(b.passed) - Number(a.passed);
            return (b.score || 0) - (a.score || 0);
        })[0]?.id
        : null;

    /* ── Loading state ── */
    if (!reviewData || (loading && !reviewData)) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 10,
                    gap: 2,
                }}
            >
                <CircularProgress sx={{ color: ORANGE }} />
                <Typography
                    sx={{
                        fontSize: "0.9rem",
                        color: TEXT_MUTED,
                        fontFamily: "'Inter',sans-serif",
                    }}
                >
                    <Langs str="VSEC_REVIEW_LOADING" />
                </Typography>
            </Box>
        );
    }

    const { quiz, questions } = reviewData;
    const correctCount = questions.filter((q) => q.your_answer_is_correct).length;
    const totalCount = questions.length;

    return (
        <>
            <style>{KEYFRAMES}</style>
            <Box sx={{ minHeight: "100%", background: BG_PAGE, pb: 6 }}>
                {/* ── Page header ── */}
                <Box
                    sx={{
                        background: "rgba(255,255,255,0.88)",
                        backdropFilter: "blur(12px)",
                        WebkitBackdropFilter: "blur(12px)",
                        borderBottom: `1px solid ${BORDER_SUBTLE}`,
                        px: isSmall ? 1.5 : { xs: 2, md: 4 },
                        py: isSmall ? 1.5 : 2,
                        display: "flex",
                        flexDirection: isSmall ? "column" : "row",
                        alignItems: isSmall ? "stretch" : "center",
                        gap: isSmall ? 1.2 : 2,
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                        flexWrap: isSmall ? "nowrap" : "wrap",
                    }}
                >
                    {/* Top row on mobile: back + title + score chip */}
                    <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: isSmall ? 1 : 2,
                        width: isSmall ? "100%" : "auto",
                        flex: isSmall ? "none" : undefined,
                    }}>
                        {/* Back button */}
                        <Button
                            onClick={onBack}
                            startIcon={<ArrowBackRoundedIcon sx={{ fontSize: "18px !important" }} />}
                            sx={{
                                borderRadius: "11px",
                                px: isSmall ? 1.5 : 2,
                                py: 0.8,
                                minHeight: 44,
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "0.86rem",
                                fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                                color: TEXT_SECONDARY,
                                background: "rgba(0,0,0,0.04)",
                                border: "1px solid rgba(0,0,0,0.06)",
                                transition: `all 250ms ${EASE}`,
                                flexShrink: 0,
                                "&:hover": {
                                    background: "rgba(0,0,0,0.07)",
                                    transform: "translateX(-2px)",
                                },
                            }}
                        >
                            {isSmall ? "" : Localize("VSEC_REVIEW_BACK", lang)}
                        </Button>

                        {/* Quiz icon */}
                        <Box
                            sx={{
                                width: 36,
                                height: 36,
                                borderRadius: "10px",
                                background: ORANGE_LIGHT,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                            }}
                        >
                            <QuizOutlinedIcon sx={{ fontSize: 20, color: ORANGE }} />
                        </Box>

                        {/* Title */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontSize: isSmall ? "0.88rem" : { xs: "0.95rem", md: "1.05rem" },
                                    fontWeight: 650,
                                    color: TEXT_PRIMARY,
                                    letterSpacing: "-0.02em",
                                    fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                {quiz?.title ? `${quiz.title} — ${Localize("VSEC_REVIEW_REVISION_SUFFIX", lang)}` : Localize("VSEC_REVIEW_REVISION_SUFFIX", lang)}
                            </Typography>
                        </Box>

                        {/* Score summary chip */}
                        <Chip
                            label={`${correctCount} / ${totalCount}`}
                            size="small"
                            sx={{
                                fontSize: "0.78rem",
                                fontWeight: 650,
                                fontFamily: "'Inter',sans-serif",
                                color: ORANGE,
                                background: ORANGE_LIGHT,
                                border: "1px solid rgba(255,144,21,0.2)",
                                borderRadius: "9px",
                                height: 26,
                                flexShrink: 0,
                                "& .MuiChip-label": { px: 1.4 },
                            }}
                        />
                    </Box>

                    {/* Attempt selector (on separate row for mobile if multiple attempts) */}
                    {collabQuizAttempts.length > 1 && (
                        <Box sx={{ width: isSmall ? "100%" : "auto" }}>
                            <AttemptSelect
                                attempts={collabQuizAttempts}
                                selectedAttemptId={selectedAttemptId}
                                bestAttemptId={bestAttemptId}
                                allowScores={allowScores}
                                onChange={(id) => setSelectedAttemptId(id)}
                                isSmall={isSmall}
                                lang={lang}
                            />
                        </Box>
                    )}
                </Box>

                {/* ── Questions list ── */}
                <Box
                    sx={{
                        maxWidth: 760,
                        mx: "auto",
                        px: isSmall ? 1.5 : { xs: 1.5, sm: 3, md: 0 },
                        pt: isSmall ? 2 : 3,
                    }}
                >
                    {questions.map((question, idx) => (
                        <Box key={idx}>
                            <QuestionReviewCard
                                question={question}
                                index={idx}
                                animationDelay={idx * 60}
                                isSmall={isSmall}
                                lang={lang}
                            />
                            {idx < questions.length - 1 ? (
                                <Divider sx={{ my: 0, borderColor: "transparent" }} />
                            ) : null}
                        </Box>
                    ))}
                </Box>
            </Box>
        </>
    );
}

/**
 * AttemptSelect – A compact dropdown to pick which attempt to review.
 */
function AttemptSelect({ attempts, selectedAttemptId, bestAttemptId, allowScores, onChange, isSmall, lang }) {
    moment.locale("fr");

    return (
        <FormControl size="small" sx={{ minWidth: isSmall ? "100%" : 180, flexShrink: 0 }}>
            <Select
                value={selectedAttemptId || ""}
                onChange={(e) => onChange(e.target.value)}
                displayEmpty
                sx={{
                    borderRadius: "11px",
                    fontSize: isSmall ? "16px" : "0.82rem",
                    fontFamily: "'Inter',sans-serif",
                    fontWeight: 600,
                    color: TEXT_PRIMARY,
                    background: "rgba(255,144,21,0.06)",
                    border: "1px solid rgba(255,144,21,0.22)",
                    minHeight: isSmall ? 48 : "auto",
                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                    "&:hover": { background: "rgba(255,144,21,0.10)" },
                    "& .MuiSelect-icon": { color: ORANGE },
                }}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            borderRadius: "14px",
                            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                            mt: 0.5,
                        },
                    },
                    sx: isSmall ? { zIndex: 1500 } : {},
                }}
            >
                {attempts.map((attempt) => {
                    const isBest = attempt.id === bestAttemptId;
                    const dateStr = attempt.started_at
                        ? moment(attempt.started_at).format("D MMM YYYY")
                        : "—";
                    const passedColor = attempt.passed ? GREEN : "#dc2626";
                    return (
                        <MenuItem
                            key={attempt.id}
                            value={attempt.id}
                            sx={{
                                fontSize: "0.82rem",
                                fontFamily: "'Inter',sans-serif",
                                fontWeight: 500,
                                borderRadius: "8px",
                                mx: 0.5,
                                my: 0.2,
                                gap: 1,
                                minHeight: isSmall ? 44 : "auto",
                                "&.Mui-selected": {
                                    background: ORANGE_LIGHT,
                                    color: ORANGE,
                                    fontWeight: 650,
                                },
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
                                {/* Attempt number badge */}
                                <Box
                                    sx={{
                                        minWidth: 24,
                                        height: 22,
                                        borderRadius: "6px",
                                        background: isBest ? "rgba(255,144,21,0.12)" : "rgba(0,0,0,0.06)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "0.7rem",
                                            fontWeight: 700,
                                            color: isBest ? ORANGE : TEXT_MUTED,
                                            fontFamily: "'Inter',sans-serif",
                                        }}
                                    >
                                        #{attempt.attempt_number}
                                    </Typography>
                                </Box>

                                {/* Date */}
                                <Typography sx={{ fontSize: "0.8rem", color: TEXT_SECONDARY, fontFamily: "'Inter',sans-serif", flex: 1 }}>
                                    {dateStr}
                                </Typography>

                                {/* Score (if allowed) */}
                                {allowScores && (
                                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 650, color: passedColor, fontFamily: "'Inter',sans-serif" }}>
                                        {attempt.score}%
                                    </Typography>
                                )}

                                {/* Pass/fail dot */}
                                <Box
                                    sx={{
                                        width: 7,
                                        height: 7,
                                        borderRadius: "50%",
                                        background: passedColor,
                                        flexShrink: 0,
                                    }}
                                />

                                {/* Best badge */}
                                {isBest && (
                                    <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: ORANGE, fontFamily: "'Inter',sans-serif", flexShrink: 0 }}>
                                        ★
                                    </Typography>
                                )}
                            </Box>
                        </MenuItem>
                    );
                })}
            </Select>
        </FormControl>
    );
}

/**
 * QuestionReviewCard – Renders a single reviewed question with all its options,
 * a correctness indicator, and an explanation box.
 */
function QuestionReviewCard({ question, index, animationDelay, isSmall, lang }) {
    const { question_text, your_answer_is_correct, explanation, options } = question;

    return (
        <Paper
            elevation={0}
            sx={{
                mb: 2.5,
                borderRadius: isSmall ? "14px" : "18px",
                border: `1px solid ${your_answer_is_correct
                    ? "rgba(22,163,74,0.18)"
                    : "rgba(220,38,38,0.15)"}`,
                background: "#ffffff",
                overflow: "hidden",
                position: "relative",
                animation: `qrFadeInUp 500ms ${EASE} ${animationDelay}ms both`,
                /* Coloured left accent bar */
                "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    bottom: 0,
                    width: "4px",
                    background: your_answer_is_correct
                        ? `linear-gradient(180deg, ${GREEN}, rgba(22,163,74,0.4))`
                        : `linear-gradient(180deg, ${RED}, rgba(220,38,38,0.4))`,
                    borderRadius: "18px 0 0 18px",
                },
            }}
        >
            {/* ── Question header ── */}
            <Box sx={{
                pl: isSmall ? "calc(4px + 1rem)" : "calc(4px + 1.5rem)",
                pr: isSmall ? 2 : 2.5,
                pt: isSmall ? 2 : 2.5,
                pb: 1.5,
            }}>
                <Stack direction="row" alignItems="flex-start" spacing={isSmall ? 1 : 1.5}>
                    {/* Question number chip */}
                    <Chip
                        label={index + 1}
                        size="small"
                        sx={{
                            minWidth: 28,
                            height: 26,
                            borderRadius: "8px",
                            fontWeight: 700,
                            fontSize: "0.76rem",
                            fontFamily: "'Inter',sans-serif",
                            mt: 0.2,
                            flexShrink: 0,
                            color: your_answer_is_correct ? GREEN : RED,
                            background: your_answer_is_correct ? GREEN_LIGHT : RED_LIGHT,
                            border: `1px solid ${your_answer_is_correct
                                ? "rgba(22,163,74,0.18)"
                                : "rgba(220,38,38,0.15)"}`,
                            "& .MuiChip-label": { px: 0.8 },
                        }}
                    />

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        {/* Question text */}
                        <Typography
                            variant="h6"
                            sx={{
                                fontSize: isSmall ? "0.9rem" : { xs: "0.95rem", md: "1rem" },
                                fontWeight: 620,
                                color: TEXT_PRIMARY,
                                letterSpacing: "-0.015em",
                                lineHeight: 1.45,
                                fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                            }}
                        >
                            {question_text}
                        </Typography>

                        {/* Correctness badge */}
                        <Stack direction="row" alignItems="center" spacing={0.6} sx={{ mt: 0.8 }}>
                            {your_answer_is_correct ? (
                                <>
                                    <CheckCircleRoundedIcon sx={{ fontSize: 16, color: GREEN }} />
                                    <Typography
                                        sx={{
                                            fontSize: "0.78rem",
                                            fontWeight: 600,
                                            color: GREEN,
                                            fontFamily: "'Inter',sans-serif",
                                        }}
                                    >
                                        {Localize("VSEC_REVIEW_CORRECT", lang)}
                                    </Typography>
                                </>
                            ) : (
                                <>
                                    <CancelRoundedIcon sx={{ fontSize: 16, color: RED }} />
                                    <Typography
                                        sx={{
                                            fontSize: "0.78rem",
                                            fontWeight: 600,
                                            color: RED,
                                            fontFamily: "'Inter',sans-serif",
                                        }}
                                    >
                                        {Localize("VSEC_REVIEW_INCORRECT", lang)}
                                    </Typography>
                                </>
                            )}
                        </Stack>
                    </Box>
                </Stack>
            </Box>

            <Divider sx={{ mx: isSmall ? 1.5 : 2.5, borderColor: BORDER_SUBTLE }} />

            {/* ── Options ── */}
            <Box sx={{
                pl: isSmall ? "calc(4px + 1rem)" : "calc(4px + 1.5rem)",
                pr: isSmall ? 1.5 : 2.5,
                py: 1.5,
            }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
                    {options.map((option, oidx) => (
                        <OptionRow key={oidx} option={option} isSmall={isSmall} lang={lang} />
                    ))}
                </Box>
            </Box>

            {/* ── Explanation box ── */}
            {explanation ? (
                <Box sx={{
                    pl: isSmall ? "calc(4px + 1rem)" : "calc(4px + 1.5rem)",
                    pr: isSmall ? 1.5 : 2.5,
                    pb: 2.5,
                    pt: 0.5,
                }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 1,
                            background: "rgba(0,0,0,0.03)",
                            border: "1px solid rgba(0,0,0,0.07)",
                            borderRadius: "12px",
                            px: isSmall ? 1.5 : 2,
                            py: 1.5,
                        }}
                    >
                        <LightbulbOutlinedIcon
                            sx={{ fontSize: 16, color: ORANGE, mt: 0.25, flexShrink: 0 }}
                        />
                        <Typography
                            sx={{
                                fontSize: isSmall ? "0.82rem" : "0.84rem",
                                color: TEXT_SECONDARY,
                                fontStyle: "italic",
                                lineHeight: 1.55,
                                fontFamily: "'Inter',sans-serif",
                            }}
                        >
                            <Box
                                component="span"
                                sx={{ fontWeight: 600, fontStyle: "normal", color: TEXT_PRIMARY }}
                            >
                                {Localize("VSEC_REVIEW_EXPLANATION_LABEL", lang)}{" "}
                            </Box>
                            {explanation}
                        </Typography>
                    </Box>
                </Box>
            ) : null}
        </Paper>
    );
}

/**
 * OptionRow – Renders a single answer option with appropriate visual treatment.
 */
function OptionRow({ option, isSmall, lang }) {
    const { option_text, is_correct, you_selected } = option;

    const isSelectedCorrect = you_selected && is_correct;
    const isSelectedWrong = you_selected && !is_correct;
    const isUnselectedCorrect = !you_selected && is_correct;

    let bg = "transparent";
    let border = "rgba(0,0,0,0.05)";
    let textColor = TEXT_SECONDARY;
    let textDecoration = "none";
    let iconNode = null;

    if (isSelectedCorrect) {
        bg = BLUE_LIGHT;
        border = "rgba(29,78,216,0.2)";
        textColor = BLUE;
        iconNode = <CheckRoundedIcon sx={{ fontSize: 15, color: BLUE, flexShrink: 0 }} />;
    } else if (isUnselectedCorrect) {
        bg = GREEN_LIGHT;
        border = "rgba(22,163,74,0.2)";
        textColor = GREEN;
        iconNode = <CheckRoundedIcon sx={{ fontSize: 15, color: GREEN, flexShrink: 0 }} />;
    } else if (isSelectedWrong) {
        bg = RED_LIGHT;
        border = "rgba(220,38,38,0.15)";
        textColor = RED;
        textDecoration = "line-through";
        iconNode = <CloseRoundedIcon sx={{ fontSize: 15, color: RED, flexShrink: 0 }} />;
    }

    return (
        <Stack
            direction="row"
            alignItems={isSmall ? "flex-start" : "center"}
            spacing={1}
            sx={{
                px: 1.5,
                py: isSmall ? 1 : 0.9,
                borderRadius: "10px",
                background: bg,
                border: `1px solid ${border}`,
                transition: `all 200ms ${EASE}`,
                minHeight: isSmall ? 44 : "auto",
            }}
        >
            {/* Option icon or neutral bullet */}
            {iconNode || (
                <Box
                    sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: "rgba(0,0,0,0.18)",
                        flexShrink: 0,
                        ml: 0.3,
                        mt: isSmall ? 0.8 : 0,
                    }}
                />
            )}

            {/* Option text */}
            <Typography
                sx={{
                    fontSize: isSmall ? "0.85rem" : "0.88rem",
                    fontWeight: (isSelectedCorrect || isUnselectedCorrect) ? 600 : 420,
                    color: textColor,
                    textDecoration,
                    fontFamily: "'Inter',sans-serif",
                    lineHeight: 1.4,
                    flex: 1,
                }}
            >
                {option_text}
            </Typography>

            {/* "Bonne réponse" label */}
            {is_correct ? (
                <Chip
                    label={Localize("VSEC_REVIEW_GOOD_ANSWER", lang)}
                    size="small"
                    sx={{
                        fontSize: isSmall ? "0.65rem" : "0.68rem",
                        fontWeight: 650,
                        fontFamily: "'Inter',sans-serif",
                        color: isSelectedCorrect ? BLUE : GREEN,
                        background: isSelectedCorrect
                            ? "rgba(29,78,216,0.10)"
                            : "rgba(22,163,74,0.10)",
                        border: `1px solid ${isSelectedCorrect
                            ? "rgba(29,78,216,0.18)"
                            : "rgba(22,163,74,0.18)"}`,
                        borderRadius: "6px",
                        height: 20,
                        flexShrink: 0,
                        "& .MuiChip-label": { px: 0.9 },
                    }}
                />
            ) : null}
        </Stack>
    );
}
